import React, { useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  BottomNavigation,
  BottomNavigationAction,
  Breadcrumbs,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import { InfoOutlined, NavigateNext } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import CommonBox from '../parts/CommonBox/CommonBox';
import DefaultInfo from './DefaultInfo';
import LiveLink from './LiveLink';
import SecuritySetting from './SecuritySetting';
import ChatSetting from './ChatSetting';
import LiveEvent from './LiveEvent';
import { LiveRegisterProps, InputData } from '../../props/LiveRegister';
import ProgressModal from '../parts/ProgressModal/ProgressModal';
import {
  EDITIONS,
  ROUTE,
  getPath,
  UI_STATUS,
  MENUBAR,
  LIVE_TAB_NAME,
  LIVE_REQUIRED_KEY,
} from '../../utils/Const';
import { RootState } from '../../app/store';
import { smoothScrollToId } from '../../utils/SmoothScroll';

/** スタイル */
const useStyle = makeStyles({
  root: {},
  header: {
    fontSize: 24,
    margin: '20px 0',
    color: '#444444',
  },
  container: {
    background: '#FFFFFF',
    border: '1px solid #F0F0F0',
    boxShadow: '0px 4px 12px #1C385E0C',
    borderRadius: 8,
    padding: '50px 70px 40px',
    '& .MuiGrid-grid-xs-3': {
      maxWidth: 350,
      flexBasis: 350,
      fontWeight: 500,
      paddingRight: 48,
    },
    '& .MuiGrid-grid-xs-9': {
      maxWidth: 'calc(100% - 380px)',
      flexBasis: 'calc(100% - 380px)',
      paddingLeft: 30,
      paddingRight: 2,
    },
    '& .MuiFormControlLabel-root .MuiTypography-root': {
      fontSize: 15,
      fontWeight: 500,
    },
  },
  navigation: {
    marginBottom: 30,
    '& .MuiBottomNavigationAction-root': {
      maxWidth: 'calc(100% / 5)',
      borderBottom: '2px solid #F0F0F0',
      alignItems: 'start',
      padding: 0,
      height: 44,
      '&.Mui-selected': {
        borderColor: '#61B227',
        opacity: 1,
      },
    },
    '& .MuiBottomNavigationAction-label': {
      color: '#333333',
      fontSize: 16,
      fontWeight: 700,
      opacity: 0.4,
      '&.Mui-selected': {
        opacity: 1,
      },
    },
  },
  buttons: {
    display: 'flex',
    justifyContent: 'end',
    '& a, button': {
      fontSize: 17,
      borderRadius: 4,
      fontWeight: 500,
      transition: 'all 400ms',
    },
    '& a': {
      background: '#D8D8D8',
      color: '#333333',
      borderBottom: '1px solid #AAAAAA',
      width: 140,
      marginRight: 15,
      textDecoration: 'none',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      '&:hover': {
        background: '#F4F4F4',
      },
    },
    '& button': {
      background: '#4A8BFB',
      borderBottom: '1px solid #3C72D0',
      color: '#FFFFFF',
      width: 170,
      '&:hover': {
        backgroundColor: '#015CFA',
      },
    },
  },
  hidden: {
    display: 'none',
  },
  footer: {
    borderTop: '1px dashed #B9B9B9',
    paddingTop: 30,
    marginTop: 30,
  },
  securityAttention: {
    display: 'flex',
    background: '#C3273305',
    border: '1px solid #C32733',
    borderRadius: 4,
    color: '#C32733',
    padding: 10,
    marginBottom: 15,
  },
  icon: {
    color: '#C32733',
    marginRight: 4,
    paddingTop: 3,
  },
  breadcrumbs: {
    marginTop: 30,
    fontSize: 14,
    '& a': {
      color: '#333333',
    },
  },
});

/** ライブの新規登録(UI) */
const LiveRegisterComponent = (props: LiveRegisterProps): JSX.Element => {
  const classes = useStyle();
  const status = useSelector((state: RootState) => state.ui.status);
  const user = useSelector((state: RootState) => state.user);
  const { edition, saveLiveProfile } = props;
  const [selectTab, setSelectTab] = useState<LIVE_TAB_NAME>(
    LIVE_TAB_NAME.DEFAULT_INFO
  );
  const [saveDisableFlg, setSaveDisableFlg] = useState(false);

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    watch,
    clearErrors,
    setError,
    setValue,
    trigger,
  } = useForm({
    mode: 'all',
    criteriaMode: 'all',
    shouldFocusError: false,
  });

  const onError = (errors: FieldValues) => {
    if (Object.keys(errors).length <= 0) return;
    const errorsKey = Object.keys(errors);
    const errorsKeySet = new Set(errorsKey);
    const sortedErrorsKey = LIVE_REQUIRED_KEY.filter((value) =>
      errorsKeySet.has(value)
    );
    // 一致するものがあればそのタブに移動
    const base = sortedErrorsKey.find((x) => x.indexOf('BaseSetting') >= 0);
    if (base) {
      const baseSettingArray = sortedErrorsKey.filter((item) =>
        item.includes('BaseSetting')
      );
      setSelectTab(LIVE_TAB_NAME.DEFAULT_INFO);
      smoothScrollToId(baseSettingArray[0]);
      return;
    }
    const liveLink = sortedErrorsKey.find(
      (x) => x.indexOf('ExLinkSetting') >= 0
    );
    if (liveLink) {
      const exLinkSettingArray = sortedErrorsKey.filter((item) =>
        item.includes('ExLinkSetting')
      );
      setSelectTab(LIVE_TAB_NAME.LIVE_LINK);
      smoothScrollToId(exLinkSettingArray[0]);
      return;
    }
    const chat = sortedErrorsKey.find((x) => x.indexOf('ChatSetting') >= 0);
    if (chat) {
      const chatSettingArray = sortedErrorsKey.filter((item) =>
        item.includes('ChatSetting')
      );
      setSelectTab(LIVE_TAB_NAME.CHAT_SETTING);
      smoothScrollToId(chatSettingArray[0]);
    }
  };

  // 保存開始時に要素を非活性にする
  useEffect(() => {
    if (status.liveSaveButton === UI_STATUS.SAVING) {
      setSaveDisableFlg(true);
    } else {
      setSaveDisableFlg(false);
    }
  }, [status.liveSaveButton]);

  // 処理中
  if (status.liveRegister === UI_STATUS.PROCESSING) return <LinearProgress />;

  // 保存中
  if (status.liveRegister === UI_STATUS.SAVING)
    return (
      <>
        <LinearProgress />
        <ProgressModal
          open
          title="ライブ設定を保存中です。"
          info="保存には数秒かかる場合がございます。"
        />
      </>
    );

  // 更新完了(ライブの設定設定と開催画面へリダイレクト)
  if (status.liveRegister === UI_STATUS.REDIRECT)
    return (
      <Redirect
        to={{
          pathname: getPath(ROUTE.LIVE_LIST),
          search: `?c=${user.contract.cid}&t=${user.contract.readAPIToken}&u=${user.user}`,
        }}
      />
    );

  return (
    <CommonBox navibar={MENUBAR.LIVELIST}>
      <h2 className={classes.header}>ライブ配信設定 新規登録</h2>
      <Box className={classes.container}>
        <BottomNavigation
          showLabels
          value={selectTab}
          onChange={(event, newValue) => {
            setSelectTab(newValue);
          }}
          className={classes.navigation}
        >
          <BottomNavigationAction
            label="基本設定"
            value={LIVE_TAB_NAME.DEFAULT_INFO}
          />
          <BottomNavigationAction
            label="動画からのリンク"
            value={LIVE_TAB_NAME.LIVE_LINK}
          />
          <BottomNavigationAction
            label="セキュリティ"
            value={LIVE_TAB_NAME.SECURITY_SETTING}
          />
          <BottomNavigationAction
            label="チャット設定"
            value={LIVE_TAB_NAME.CHAT_SETTING}
          />
          {(edition === EDITIONS.BUSINESS ||
            edition === EDITIONS.EXPERT ||
            edition === EDITIONS.EXPERT_2 ||
            edition === EDITIONS.ENTERPRISE) && (
            <BottomNavigationAction
              label="ライブイベント情報"
              value={LIVE_TAB_NAME.LIVE_EVENT}
            />
          )}
        </BottomNavigation>
        <form
          onSubmit={handleSubmit(
            (data: InputData) =>
              saveLiveProfile(data, setError, onError, errors),
            onError
          )}
        >
          <DefaultInfo
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            clearErrors={clearErrors}
            setError={setError}
            setValue={setValue}
            RegisterProps={props}
            open={selectTab === LIVE_TAB_NAME.DEFAULT_INFO}
          />
          <LiveLink
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            trigger={trigger}
            clearErrors={clearErrors}
            setError={setError}
            open={selectTab === LIVE_TAB_NAME.LIVE_LINK}
          />
          <SecuritySetting
            control={control}
            watch={watch}
            RegisterProps={props}
            open={selectTab === LIVE_TAB_NAME.SECURITY_SETTING}
          />
          <ChatSetting
            control={control}
            register={register}
            errors={errors}
            watch={watch}
            clearErrors={clearErrors}
            setError={setError}
            setValue={setValue}
            trigger={trigger}
            RegisterProps={props}
            open={selectTab === LIVE_TAB_NAME.CHAT_SETTING}
          />
          <LiveEvent
            register={register}
            open={selectTab === LIVE_TAB_NAME.LIVE_EVENT}
          />
          <Box className={classes.footer}>
            {selectTab === LIVE_TAB_NAME.SECURITY_SETTING && (
              <Box className={classes.securityAttention}>
                <InfoOutlined fontSize="small" className={classes.icon} />
                <Box>
                  当セキュリティ機能は、設定後10～30分程度で有効となります。設定直後は制限がかかりません。また、ライブ配信設定の登録直後に当設定を行われた場合、設定が有効になるまでの間は当該ライブが視聴できない状態となります。
                </Box>
              </Box>
            )}
            <Box className={classes.buttons}>
              <Link
                to={`${getPath(ROUTE.LIVE_LIST)}?c=${user.contract.cid}&t=${
                  user.contract.readAPIToken
                }&u=${user.user}`}
              >
                一覧へ戻る
              </Link>
              <Button type="submit" disabled={saveDisableFlg}>
                {saveDisableFlg ? '保存中' : '保存'}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        className={classes.breadcrumbs}
      >
        {[
          <Link
            to={`${getPath(ROUTE.LIVE_LIST)}?c=${user.contract.cid}&t=${
              user.contract.readAPIToken
            }&u=${user.user}`}
          >
            ライブの配信設定と開催
          </Link>,
          <Typography style={{ fontSize: 14 }}>新規登録</Typography>,
        ]}
      </Breadcrumbs>
    </CommonBox>
  );
};

export default LiveRegisterComponent;
